import React, { useRef } from 'react';
import { arrayOf, shape, string, func, node } from 'prop-types';
import { Card } from '@thd-olt-component-react/card';
import { Row, Col } from '@thd-olt-component-react/grid';
import { useProduct } from '@thd-nucleus/data-sources';
import { FirstReview } from '../FirstReview/FirstReview.component';
import { Overview } from '../Overview/Overview';
import { WriteAReviewForm } from '../WriteAReview/components/WriteAReviewForm/WriteAReviewForm';
import './RatingsAndReviewsMobile.scss';
import { CustomerImageCarousel } from '../CustomerImageCarousel/CustomerImageCarousel.component';
import { MobileOverlay } from './MobileOverlay';
import { Review } from '../Review/Review.component';

// TODO: Removed Pros & Cons b/c the functionality doesn't currently do anything

export const RatingsAndReviewsMobile = ({
  apiKey,
  filterCheckboxes,
  children,
  itemId,
  mobileReviewsOnFirstPage,
  onDeselectFilter,
  onFilterChange,
  onPageChange,
  reviewModels,
  reviewStatistics,
  selected,
  searchTerm,
  sortBy,
  onSearch,
  onSortChange,
  selectedSentiment,
  target
}) => {

  if (!(reviewModels?.length || reviewStatistics?.currentSku)) {
    return (
      <Card noHide>
        <Card.Content maxHeight="none">
          <FirstReview
            reviewStatistics={reviewStatistics}
            itemId={itemId}
            apiKey={apiKey}
            className="outline"
          />
        </Card.Content>
      </Card>
    );
  }

  const cardClickRef = useRef();
  const { data, error, loading } = useProduct({ itemId, domains: ['identifiers', 'mediaSlim'] });
  if ((error && !data) || !data || loading) {
    return null;
  }
  const { product } = data || {};
  const { identifiers, media } = product || {};
  const { brandName = '', productLabel = '' } = identifiers || {};
  const imageUrl = !!(media?.image?.url && media?.images?.[0])
    && media?.image?.url?.replace(/1000/, media?.image?.sizes?.[0]);

  if (target === 'productDetailsRating') {
    return (
      <Card noHide className="card-rounded-corners ratings-and-reviews-mobile">
        <Card.Content>
          <Row>
            <Col xs="12" className="mobile-get-all-col">
              {children}
            </Col>
          </Row>
        </Card.Content>
      </Card>
    );
  }

  const isEventBusCreated = () => {
    return typeof window !== 'undefined' && window.LIFE_CYCLE_EVENT_BUS;
  };

  const closeCard = (scrollToBottom = false) => {
    if (typeof window !== 'undefined' && window.LIFE_CYCLE_EVENT_BUS) {
      window.LIFE_CYCLE_EVENT_BUS.trigger('ratings-reviews.card-click', { scrollToBottom });
    }
  };

  const triggerCardClick = (cardOnClick) => {
    if (!cardClickRef.current) {
      cardClickRef.current = cardOnClick;
    }
    if ((isEventBusCreated())) {
      window.LIFE_CYCLE_EVENT_BUS.on('ratings-reviews.card-click', ({ output }) => {
        const { scrollToBottom = false } = output;
        cardOnClick(this, 'all', scrollToBottom);
      });
    }
  };

  const handleCardClick = () => {
    if (cardClickRef.current) {
      cardClickRef.current(this, 'all', false);
    }
  }

  return (
    <Card
      noHide
      className="card-rounded-corners ratings-and-reviews-mobile"
      getOnClick={triggerCardClick}
    >
      <Card.Content
        title="Customer Reviews"
        maxHeight="none"
        className="ratings-and-reviews-mobile__card-content"
        onClick={() => handleCardClick()}
        underline={false}
      >
        <Overview reviewStatistics={reviewStatistics} itemId={itemId} hideSecondaryRatings />
        <Card.Content.Overlay name="all">
          <MobileOverlay
            apiKey={apiKey}
            itemId={itemId}
            reviewModels={reviewModels}
            onDeselectFilter={onDeselectFilter}
            onFilterChange={onFilterChange}
            onPageChange={onPageChange}
            filterCheckboxes={filterCheckboxes}
            reviewStatistics={reviewStatistics}
            selected={selected}
            selectedSentiment={selectedSentiment}
            brandName={brandName}
            imageUrl={imageUrl}
            productLabel={productLabel}
            onSearch={onSearch}
            searchTerm={searchTerm}
            onSortChange={onSortChange}
            sortBy={sortBy}
          />
        </Card.Content.Overlay>
        <Card.Content.Overlay
          name="writeAReview"
          overlayTitle="Write A Review"
          className="ratings-and-reviews-mobile__overlay"
        >
          <WriteAReviewForm
            apiKey={apiKey}
            itemId={itemId}
            reviewStatistics={reviewStatistics}
            product={product}
            closeOverlay={() => closeCard()}
          />
        </Card.Content.Overlay>
      </Card.Content>
      <Card.Footer>
        <CustomerImageCarousel itemId={itemId} />
        {mobileReviewsOnFirstPage.map((review, reviewIndex) => {
          return <Review
            review={review}
            id={reviewIndex}
            key={`review-${reviewIndex}`}
            apiKey={apiKey}
            shortenReviews
          />
        })}
      </Card.Footer>
      <Card.Footer
        triggerFor="all"
        className="ratings-and-reviews__card-footer"
      >
        See All Reviews
      </Card.Footer>
      <Card.Footer
        triggerFor="writeAReview"
        className="ratings-and-reviews__card-footer"
      >
        Write A Review
      </Card.Footer>
    </Card>
  );
};

RatingsAndReviewsMobile.displayName = 'RatingsAndReviewsMobile';

RatingsAndReviewsMobile.propTypes = {
  apiKey: string,
  children: node,
  itemId: string.isRequired,
  filterCheckboxes: arrayOf(shape({})).isRequired,
  mobileReviewsOnFirstPage: arrayOf(shape({})).isRequired,
  onDeselectFilter: func.isRequired,
  onFilterChange: func.isRequired,
  reviewModels: arrayOf(shape({})).isRequired,
  reviewStatistics: shape({}).isRequired,
  searchTerm: string,
  selected: arrayOf(shape({})),
  onSearch: func.isRequired,
  target: string.isRequired,
  selectedSentiment: shape({})
};

RatingsAndReviewsMobile.defaultProps = {
  apiKey: '',
  children: null,
  selected:[{}],
  selectedSentiment: {}
};
